import React, { useMemo } from 'react'
import Select, { components } from 'react-select'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import DateRangeComp from '../../../../components/dateComponent/DateRangeComp'
import moment from 'moment'
import { Tooltip } from 'react-tooltip'
import { KTSVG } from '../../../../_metronic/helpers'
import { SelectOption } from '../types'

const { Option } = components

const FILTER_CONTROL_HEIGHT = 38

// Custom Option component with standard SpendSail checkbox styling
const CheckboxOption = (props: any) => (
  <Option {...props}>
    <div className='d-flex align-items-center gap-2 ss-multi-check-option w-100 cursor-pointer'>
      <input
        type='checkbox'
        className='form-check-input ss-multi-check-option__input m-0 cursor-pointer'
        checked={!!props.isSelected}
        onChange={() => null}
        tabIndex={-1}
      />
      <label className='ss-multi-check-option__label mb-0 cursor-pointer text-truncate' title={props.label}>
        {props.label}
      </label>
    </div>
  </Option>
)

// Helper to build CustomMultiValue showing 1 chip + "+X more" popover trigger
const createCustomMultiValue = (
  title: string,
  selectedList: SelectOption[],
  onRemove: (val: number) => void,
  onClear: () => void,
  popoverId: string
) => {
  return (props: any) => {
    const { index } = props
    if (index < 1) {
      return <components.MultiValue {...props} />
    }
    if (index === 1) {
      const remainingCount = selectedList.length - 1
      return (
        <OverlayTrigger
          trigger='click'
          placement='bottom'
          rootClose
          overlay={
            <Popover id={popoverId} className='ss-selected-users-popover'>
              <Popover.Header className='d-flex align-items-center justify-content-between fs-7 fw-bold py-2 px-3'>
                <span>
                  {title} ({selectedList.length})
                </span>
                <span
                  className='text-danger fs-8 fw-semibold cursor-pointer'
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    onClear()
                  }}
                >
                  Clear All
                </span>
              </Popover.Header>
              <Popover.Body className='p-2'>
                <div className='d-flex flex-column gap-1 ss-popover-users-list'>
                  {selectedList.map((opt) => (
                    <div
                      key={String(opt.value)}
                      className='d-flex align-items-center justify-content-between gap-2 px-2 py-1 rounded ss-popover-user-item'
                    >
                      <span className='fs-8 text-truncate' title={opt.label}>
                        {opt.label}
                      </span>
                      <span
                        className='cursor-pointer text-muted hover-danger fs-7 ss-popover-remove-btn'
                        title='Remove'
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          onRemove(opt.value)
                        }}
                      >
                        ✕
                      </span>
                    </div>
                  ))}
                </div>
              </Popover.Body>
            </Popover>
          }
        >
          <span
            className='badge badge-light-primary fw-bold cursor-pointer ss-show-more-badge ms-1'
            title={`Click to view all ${title.toLowerCase()}`}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
            onMouseDown={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
          >
            +{remainingCount} more
          </span>
        </OverlayTrigger>
      )
    }
    return null
  }
}

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: FILTER_CONTROL_HEIGHT,
    height: 'auto',
    fontSize: '0.8125rem',
    borderColor: state.isFocused ? '#3182ce' : '#e2e8f0',
    boxShadow: 'none',
    '&:hover': { borderColor: '#cbd5e0' },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: '2px 8px',
    minHeight: FILTER_CONTROL_HEIGHT - 2,
    maxHeight: 68,
    overflowY: 'auto',
    gap: 2,
    flexWrap: 'wrap',
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    minHeight: FILTER_CONTROL_HEIGHT,
  }),
  placeholder: (base: any) => ({
    ...base,
    color: '#718096',
    fontSize: '0.8125rem',
  }),
  singleValue: (base: any) => ({
    ...base,
    color: '#1a202c',
    fontSize: '0.8125rem',
  }),
  multiValue: (base: any) => ({
    ...base,
    fontSize: '0.75rem',
  }),
  menu: (base: any) => ({
    ...base,
    fontSize: '0.8125rem',
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 99999,
  }),
}

const selectMenuPortalProps = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed' as const,
}

export type AnomalyFiltersState = {
  managers: SelectOption[]
  users: SelectOption[]
  categories: SelectOption[]
  periodPreset: '3' | '6' | 'custom'
  startDate: string
  endDate: string
  thresholdPercent: number
  showOnlyAnomalies: boolean
}

type Props = {
  value: AnomalyFiltersState
  onChange: (next: AnomalyFiltersState) => void
  managerOptions: SelectOption[]
  userOptions: SelectOption[]
  categoryOptions: SelectOption[]
  hideManager?: boolean
  embedded?: boolean
  onClear: () => void
  onRun: () => void
  running?: boolean
}

const AnomalyFilters: React.FC<Props> = ({
  value,
  onChange,
  managerOptions,
  userOptions,
  categoryOptions,
  hideManager,
  embedded,
  onRun,
  running,
}) => {
  const dateDisplay =
    value.startDate && value.endDate
      ? `${moment(value.startDate).format('DD-MM-YYYY')} - ${moment(value.endDate).format('DD-MM-YYYY')}`
      : ''

  const setPreset = (preset: '3' | '6') => {
    const end = moment()
    const start = moment().subtract(Number(preset) - 1, 'months').startOf('month')
    onChange({
      ...value,
      periodPreset: preset,
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
    })
  }

  const ManagersMultiValue = useMemo(
    () =>
      createCustomMultiValue(
        'Selected Managers',
        value.managers,
        (val) => onChange({ ...value, managers: value.managers.filter((m) => m.value !== val) }),
        () => onChange({ ...value, managers: [] }),
        'selected-managers-popover-anomaly'
      ),
    [value, onChange]
  )

  const UsersMultiValue = useMemo(
    () =>
      createCustomMultiValue(
        hideManager ? 'Selected Team Members' : 'Selected Users',
        value.users,
        (val) => onChange({ ...value, users: value.users.filter((u) => u.value !== val) }),
        () => onChange({ ...value, users: [] }),
        'selected-users-popover-anomaly'
      ),
    [value, onChange, hideManager]
  )

  const CategoriesMultiValue = useMemo(
    () =>
      createCustomMultiValue(
        'Selected Categories',
        value.categories,
        (val) => onChange({ ...value, categories: value.categories.filter((c) => c.value !== val) }),
        () => onChange({ ...value, categories: [] }),
        'selected-categories-popover-anomaly'
      ),
    [value, onChange]
  )

  const filtersRow = (
    <>
      <Tooltip id='anomaly-threshold-tip' place='top' />
      <div
        className={`ss-anomalies-filters-row${
          hideManager ? ' ss-anomalies-filters-row--no-manager' : ''
        }`}
      >
          {!hideManager && (
            <div className='ss-anomalies-filter-field ss-anomalies-filter-field--manager'>
              <label className='form-label mb-1'>Manager</label>
              <Select
                isMulti
                options={managerOptions}
                value={value.managers}
                onChange={(opts) => onChange({ ...value, managers: (opts as SelectOption[]) || [] })}
                placeholder='All Managers'
                classNamePrefix='react-select'
                className='filter-valueContainer ss-users-multi-select ss-filter-control ss-anomalies-filter-select'
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{ Option: CheckboxOption, MultiValue: ManagersMultiValue }}
                styles={selectStyles}
                {...selectMenuPortalProps}
              />
            </div>
          )}

          <div className='ss-anomalies-filter-field ss-anomalies-filter-field--user'>
            <label className='form-label mb-1'>{hideManager ? 'Team Member' : 'User'}</label>
            <Select
              isMulti
              options={userOptions}
              value={value.users}
              onChange={(opts) => onChange({ ...value, users: (opts as SelectOption[]) || [] })}
              placeholder={hideManager ? 'Search team member...' : 'Search user...'}
              classNamePrefix='react-select'
              className='filter-valueContainer ss-users-multi-select ss-filter-control ss-anomalies-filter-select'
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{ Option: CheckboxOption, MultiValue: UsersMultiValue }}
              styles={selectStyles}
              {...selectMenuPortalProps}
            />
          </div>

          <div className='ss-anomalies-filter-field ss-anomalies-filter-field--category'>
            <label className='form-label mb-1'>Category</label>
            <Select
              isMulti
              options={categoryOptions}
              value={value.categories}
              onChange={(opts) => onChange({ ...value, categories: (opts as SelectOption[]) || [] })}
              placeholder='All Categories'
              classNamePrefix='react-select'
              className='filter-valueContainer ss-users-multi-select ss-filter-control ss-anomalies-filter-select'
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{ Option: CheckboxOption, MultiValue: CategoriesMultiValue }}
              styles={selectStyles}
              {...selectMenuPortalProps}
            />
          </div>

          <div className='ss-anomalies-filter-field ss-anomalies-filter-field--period'>
            <label className='form-label mb-1'>Period</label>
            <div className='ss-anomalies-period-controls'>
              <div className='btn-group ss-anomalies-period-toggle' role='group' aria-label='Period preset'>
                <button
                  type='button'
                  className={`btn btn-sm ss-anomalies-period-btn ${
                    value.periodPreset === '3' ? 'btn-primary' : 'btn-light'
                  }`}
                  onClick={() => setPreset('3')}
                >
                  Last 3 Months
                </button>
                <button
                  type='button'
                  className={`btn btn-sm ss-anomalies-period-btn ${
                    value.periodPreset === '6' ? 'btn-primary' : 'btn-light'
                  }`}
                  onClick={() => setPreset('6')}
                >
                  Last 6 Months
                </button>
              </div>
              <DateRangeComp
                label=''
                className='ss-anomalies-period-date mb-0'
                startDate={value.startDate ? moment(value.startDate) : moment().subtract(5, 'months')}
                endDate={value.endDate ? moment(value.endDate) : moment()}
                value={dateDisplay}
                onApply={(_e: any, picker: any) => {
                  onChange({
                    ...value,
                    periodPreset: 'custom',
                    startDate: picker.startDate.format('YYYY-MM-DD'),
                    endDate: picker.endDate.format('YYYY-MM-DD'),
                  })
                }}
                onCancel={() => {
                  setPreset('6')
                }}
              />
            </div>
          </div>

          <div className='ss-anomalies-filter-field ss-anomalies-filter-field--threshold'>
            <label className='form-label mb-1 d-flex align-items-center gap-1'>
              Anomaly Threshold
              <span
                className='ss-anomalies-threshold-info'
                data-tooltip-id='anomaly-threshold-tip'
                data-tooltip-content='Month-over-month increase above this % is flagged as an anomaly.'
                aria-label='Threshold help'
              >
                <KTSVG path='/media/icons/duotune/general/gen045.svg' className='svg-icon-5' />
              </span>
            </label>
            <div className='input-group input-group-sm ss-anomalies-threshold-input'>
              <input
                type='number'
                className='form-control form-control-solid'
                min={0}
                max={500}
                value={value.thresholdPercent}
                onChange={(e) =>
                  onChange({ ...value, thresholdPercent: Number(e.target.value) || 0 })
                }
              />
              <span className='input-group-text'>%</span>
            </div>
          </div>

          <div className='ss-anomalies-filter-field ss-anomalies-filter-field--run'>
            <button
              type='button'
              className='btn btn-primary btn-sm fw-semibold ss-anomalies-run-btn'
              onClick={onRun}
              disabled={running}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr029.svg' className='svg-icon-3' />
              {running ? 'Running…' : 'Run Report'}
            </button>
          </div>
        </div>
    </>
  )

  if (embedded) {
    return <div className='ss-anomalies-filters-panel pb-2 mb-2 border-bottom'>{filtersRow}</div>
  }

  return (
    <div className='card ss-anomalies-filters-card mb-4'>
      <div className='card-body py-3 px-4'>{filtersRow}</div>
    </div>
  )
}

export default AnomalyFilters
